import { useEffect, useState } from "react";
import { Building2, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100";

export const ClaimView = ({ onDone }) => {
  const { t, lang } = useLanguage();
  const [mode, setMode] = useState("existant");
  const [claimable, setClaimable] = useState([]);
  const [categories, setCategories] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [slug, setSlug] = useState("");
  const [form, setForm] = useState({ name: "", category_key: "bars", neighborhood: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/pro/claimable").then((r) => setClaimable(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/establishments").then((r) => setNeighborhoods([...new Set(r.data.map((s) => s.neighborhood))].sort())).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "existant") {
        if (!slug) return;
        await api.post("/pro/claim", { slug });
        toast.success(t("Établissement lié à ton compte !"));
      } else {
        await api.post("/pro/establishments", form);
        toast.success(t("Établissement créé — bienvenue dans GoSpot Pro !"));
      }
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="pro-claim" className="mx-auto max-w-xl px-4 py-16">
      <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#7C3AED]">
        <Crown className="h-6 w-6 text-white" />
      </div>
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t("Devenir Pro")}</h1>
      <p className="mt-2 text-sm text-slate-500">
        {t("Lie ton établissement à ton compte pour gérer ta page, publier des événements et suivre tes statistiques.")}
      </p>

      <div className="mt-6 flex rounded-full bg-slate-100 p-1">
        {[
          { id: "existant", label: "Établissement existant" },
          { id: "nouveau", label: "Nouvel établissement" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`claim-tab-${t.id}`}
            onClick={() => setMode(t.id)}
            className={`flex-1 rounded-full py-2 text-sm font-semibold transition ${
              mode === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        {mode === "existant" ? (
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Choisis ton établissement")}</label>
            <select data-testid="claim-select" required value={slug} onChange={(e) => setSlug(e.target.value)} className={inputClass}>
              <option value="">{t("Choisir…")}</option>
              {claimable.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name} — {c.category_label}, {c.neighborhood}
                </option>
              ))}
            </select>
            {claimable.length === 0 && (
              <p className="mt-2 text-xs text-slate-400">{t("Tous les établissements démo sont déjà liés. Crée le tien dans « Nouvel établissement ».")}</p>
            )}
          </div>
        ) : (
          <>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Nom de l'établissement")}</label>
              <input data-testid="claim-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t("Ex. : Bar Le Vortex")} className={inputClass} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Catégorie")}</label>
                <select data-testid="claim-category" value={form.category_key} onChange={(e) => setForm((f) => ({ ...f, category_key: e.target.value }))} className={inputClass}>
                  {categories.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">{t("Quartier")}</label>
                <select data-testid="claim-neighborhood" required value={form.neighborhood} onChange={(e) => setForm((f) => ({ ...f, neighborhood: e.target.value }))} className={inputClass}>
                  <option value="">{t("Choisir…")}</option>
                  {neighborhoods.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
        <button
          type="submit"
          data-testid="claim-submit"
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] py-3.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] active:scale-[0.98] disabled:opacity-60"
        >
          <Building2 className="h-4 w-4" />
          {busy ? "Un instant…" : mode === "existant" ? "Lier cet établissement" : "Créer mon établissement"}
        </button>
      </form>
    </main>
  );
};
